import type { QueryClient } from '@tanstack/react-query';
import {
  APPOINTMENT_OUTCOMES,
  CALL_OUTCOMES,
  type Activity,
  type ActivityInput,
  type ActivityType,
} from '@aimmo/contracts';
import { resourceKeys } from '@aimmo/ui';
import { api, assertOk } from './api.js';
import { queryClient as defaultClient } from './query-client.js';

/**
 * Every screen that draws activities, by the root of its query key.
 *
 * An activity shows up in four places at once — the timeline of its subject,
 * the Agenda calendar, the reminders worklist and the dashboard's counts — and
 * each change used to refresh whichever of them its author thought of. Closing
 * a reminder from a timeline left the Agenda and the dashboard stale.
 */
const ACTIVITY_VIEWS = [['activities'], resourceKeys.all('reminders'), ['dashboard']] as const;

export async function invalidateActivityViews(client: QueryClient = defaultClient): Promise<void> {
  await Promise.all(
    ACTIVITY_VIEWS.map((queryKey) => client.invalidateQueries({ queryKey: [...queryKey] })),
  );
}

/** Marks an activity done — the one way a reminder closes — and refreshes every view of it. */
export async function completeActivity(id: string, client?: QueryClient) {
  const { body } = assertOk(
    await api.activities.complete({ params: { id }, body: { completed: true } }),
  );
  await invalidateActivityViews(client);
  return body;
}

/** What the edit dialog lets a person change; everything else is carried over. */
export interface ActivityEdits {
  type: ActivityType;
  title: string;
  body: string;
  /** An ISO instant, or `''` for none. */
  scheduledAt: string;
  endsAt: string;
  appointmentTypeId: string | null;
}

const OUTCOMES: readonly string[] = [...CALL_OUTCOMES, ...APPOINTMENT_OUTCOMES];

/**
 * Status changes are the system's own audit trail: the API refuses to edit or
 * delete one, so no screen offers it.
 */
export function isEditableActivity(activity: Pick<Activity, 'type'>): boolean {
  return activity.type !== 'status_change';
}

/**
 * The body of an edit.
 *
 * The update is a replacement, not a patch — the API writes every column from
 * the body — so whatever the dialog does not show has to travel back as it
 * was: the owner, whether and how it was completed, the extras in `meta`.
 * Leaving one out would clear it, and an edited title is no reason for a done
 * reminder to reopen or an appointment to lose its owner.
 *
 * The fields that belong to some types only follow the create dialog's rule:
 * a schedule for appointments and reminders, an end and a kind for appointments.
 */
export function activityEditInput(activity: Activity, edits: ActivityEdits): ActivityInput {
  const scheduled = edits.type === 'appointment' || edits.type === 'reminder';
  const appointment = edits.type === 'appointment';
  const title = edits.title.trim();
  const body = edits.body.trim();

  return {
    type: edits.type,
    subjectType: activity.subjectType,
    subjectId: activity.subjectId,
    userId: activity.userId ?? '',
    ...(title ? { title } : {}),
    ...(body ? { body } : {}),
    scheduledAt: scheduled ? edits.scheduledAt : '',
    endsAt: appointment ? edits.endsAt : '',
    completedAt: activity.completedAt ?? '',
    appointmentTypeId: appointment ? (edits.appointmentTypeId ?? '') : '',
    // An outcome outside today's vocabulary would be refused, and an edit that
    // cannot be saved over a value nobody can see is worse than dropping it.
    ...(activity.outcome && OUTCOMES.includes(activity.outcome)
      ? { outcome: activity.outcome as ActivityInput['outcome'] }
      : {}),
    meta: activity.meta,
  };
}

/** Saves an edit and refreshes every view of the activity. */
export async function updateActivity(id: string, input: ActivityInput, client?: QueryClient) {
  const { body } = assertOk(await api.activities.update({ params: { id }, body: input }));
  await invalidateActivityViews(client);
  return body;
}

/** Deletes an activity and refreshes every view of it. */
export async function removeActivity(id: string, client?: QueryClient): Promise<void> {
  assertOk(await api.activities.remove({ params: { id } }));
  await invalidateActivityViews(client);
}
